import { buildS3Url } from 'utils/s3Utils';

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const getOrdinalSuffix = (day) => {
  if (day >= 11 && day <= 13) {
    return 'th';
  }
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

/**
 * Format date as "27th Jan" for subscription period.
 * @param {string|null|undefined} dateStr
 * @returns {string}
 */
export const formatNfoShortDate = (dateStr) => {
  if (!dateStr) {
    return '';
  }
  const dateObj = new Date(dateStr);
  if (Number.isNaN(dateObj?.getTime?.())) {
    return '';
  }
  const day = dateObj.getDate();
  const month = MONTHS_SHORT[dateObj.getMonth()] ?? '';
  return `${day}${getOrdinalSuffix(day)} ${month}`;
};

/**
 * Format date as "09-Feb-2026" for detail rows (Figma).
 * @param {string|null|undefined} dateStr
 * @returns {string}
 */
export const formatNfoDetailDate = (dateStr) => {
  if (!dateStr) {
    return '';
  }
  const dateObj = new Date(dateStr);
  if (Number.isNaN(dateObj?.getTime?.())) {
    return '';
  }
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = MONTHS_SHORT[dateObj.getMonth()] ?? '';
  const year = dateObj.getFullYear();
  return `${day}-${month}-${year}`;
};

/**
 * @param {string|null|undefined} startDate
 * @param {string|null|undefined} endDate
 * @returns {string}
 */
export const formatNfoSubscriptionPeriod = (startDate, endDate) => {
  const formattedStart = formatNfoShortDate(startDate ?? null);
  const formattedEnd = formatNfoShortDate(endDate ?? null);

  if (formattedStart && formattedEnd) {
    return `Subscription period ${formattedStart} - ${formattedEnd}`;
  }
  if (formattedStart) {
    return `Subscription period ${formattedStart}`;
  }
  if (formattedEnd) {
    return `Subscription period until ${formattedEnd}`;
  }
  return '';
};

const LOGO_COLORS = [
  '#E85D2B',
  '#1A8FE3',
  '#C41E3A',
  '#004C8F',
  '#F58220',
  '#1A5C3A',
  '#6A1B9A',
  '#00897B',
  '#D81B60',
  '#3949AB',
];

const PLAN_SUFFIX_REGEX =
  /\s*-\s*(Direct|Regular)\s+Plan(\s*-\s*(Growth|IDCW(?:-P|-Reinvest)?|Div(?:idend)?(?:\s+Payout)?|Reinvestment?))?\s*$/i;

/**
 * Strip plan suffixes from fundName (matches mobile cleanFundName).
 * @param {string|null|undefined} name
 * @returns {string}
 */
export const cleanFundName = (name) => {
  if (!name) {
    return '';
  }
  return String(name ?? '').replace(PLAN_SUFFIX_REGEX, '').trim();
};

/**
 * @param {string|null|undefined} name
 * @param {string|null|undefined} amcName
 * @returns {string}
 */
export const getNfoLogoLetter = (name, amcName) => {
  const source = (name ?? amcName ?? 'MF').trim();
  const words = source.split(/\s+/).filter(Boolean);

  if ((words?.length ?? 0) >= 2) {
    return `${words[0]?.charAt(0) ?? ''}${words[1]?.charAt(0) ?? ''}`.toUpperCase();
  }

  return source.slice(0, 2).toUpperCase();
};

/**
 * @param {string|null|undefined} name
 * @returns {string}
 */
export const getNfoLogoColor = (name) => {
  const str = name ?? 'MF';
  let hash = 0;

  for (let i = 0; i < str.length; i += 1) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  return LOGO_COLORS[Math.abs(hash) % LOGO_COLORS.length] ?? LOGO_COLORS[0];
};

/**
 * @param {number|null|undefined} minSubsAmount
 * @param {number|null|undefined} minInvestment
 * @returns {string}
 */
export const formatNfoMinInvestment = (minSubsAmount, minInvestment) => {
  const value = minSubsAmount ?? minInvestment ?? null;

  if (value === null || value === undefined || Number(value) <= 0) {
    return '';
  }

  return `₹${Number(value ?? 0).toLocaleString('en-IN')}`;
};

/**
 * @param {string|null|undefined} dateStr
 * @returns {string}
 */
export const formatNfoDisplayDate = (dateStr) => {
  if (!dateStr) {
    return '';
  }

  const dateObj = new Date(dateStr);

  if (Number.isNaN(dateObj?.getTime?.())) {
    return '';
  }

  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * @param {Object|null|undefined} fund
 * @returns {string}
 */
export const getNfoSchemeCategory = (fund) => {
  if (!fund) {
    return '';
  }

  const joined = [fund?.fundCategory, fund?.subCategoryName]
    .filter((part) => part !== null && part !== undefined && String(part ?? '').trim() !== '')
    .filter((part, index, arr) => arr.indexOf(part) === index)
    .join(' - ');

  return joined || fund?.categoryName || '';
};

/**
 * Parse single NFO fund from API response (matches mobile parser).
 * @param {Object} fund - Raw API fund object
 * @returns {Object|null}
 */
export const parseNfoFundItem = (fund) => {
  if (!fund || typeof fund !== 'object') {
    return null;
  }

  const fundId = fund?.fundId ?? null;

  if (!fundId) {
    return null;
  }

  const rawName = fund?.fundName ?? fund?.morningStarName ?? '';
  const cleanedName = cleanFundName(rawName);
  const name = cleanedName || fund?.morningStarName || rawName || '';
  const displayName = fund?.fundName ?? fund?.morningStarName ?? name ?? '';
  const schemeName = fund?.morningStarName ?? fund?.fundName ?? name ?? '';
  const mutualFund = fund?.amcName ?? '';
  const fundHouse = fund?.amcName ?? '';
  const minInvestmentRaw = fund?.min_subs_amount ?? fund?.minInvestment ?? null;
  const minInvestmentFormatted = formatNfoMinInvestment(
    fund?.min_subs_amount ?? null,
    fund?.minInvestment ?? null
  );

  const openDate = formatNfoDisplayDate(fund?.nfoStartDate ?? null);
  const closeDate = formatNfoDisplayDate(fund?.nfoEndDate ?? null);
  const launchDateDetail = formatNfoDetailDate(fund?.nfoStartDate ?? null);
  const offerClosureDateDetail = formatNfoDetailDate(fund?.nfoEndDate ?? null);
  const subscriptionPeriod = formatNfoSubscriptionPeriod(
    fund?.nfoStartDate ?? null,
    fund?.nfoEndDate ?? null
  );
  const schemeCategory = getNfoSchemeCategory(fund);
  const logoUri = buildS3Url(fund?.amcLogo ?? fund?.fundLogo ?? null) ?? null;
  const logoLetter = getNfoLogoLetter(name, fundHouse);
  const logoColor = getNfoLogoColor(name || fundHouse);
  const linkValue = fund?.link ?? null;

  return {
    id: fundId ?? '',
    fundId: fundId ?? '',
    schemeId: fund?.scheme_id ?? null,
    name: name ?? '',
    displayName: displayName ?? '',
    schemeName: schemeName ?? '',
    mutualFund: mutualFund ?? '',
    fundHouse: fundHouse ?? '',
    minInvestment: minInvestmentFormatted ?? '',
    minInvestmentRaw:
      minInvestmentRaw !== null &&
      minInvestmentRaw !== undefined &&
      Number(minInvestmentRaw ?? 0) > 0
        ? Number(minInvestmentRaw ?? 0)
        : null,
    openDate: openDate ?? '',
    closeDate: closeDate ?? '',
    launchDate: launchDateDetail ?? '',
    offerClosureDate: offerClosureDateDetail ?? '',
    earliestClosureDate: '',
    subscriptionPeriod: subscriptionPeriod ?? '',
    logoUri: logoUri ?? null,
    logoLetter: logoLetter ?? 'MF',
    logoColor: logoColor ?? LOGO_COLORS[0],
    description: fund?.description ?? '',
    schemeType: fund?.categoryName ?? '',
    schemeCategory: schemeCategory ?? '',
    entryLoad: fund?.entryLoad ?? null,
    exitLoad: fund?.exitLoad ?? null,
    minSubscription: minInvestmentFormatted ?? '',
    link: linkValue ?? null,
    categoryName: fund?.categoryName ?? '',
    subCategoryName: fund?.subCategoryName ?? '',
    fundCategory: fund?.fundCategory ?? '',
    optionId: fund?.optionId ?? null,
    amcId: fund?.amcId ?? null,
    nfoStartDate: fund?.nfoStartDate ?? null,
    nfoEndDate: fund?.nfoEndDate ?? null,
  };
};

/**
 * Parse NFO funds API response.
 * POST /api/mutual-funds/nfo-funds
 *
 * @param {Object} response - Raw API response
 * @returns {Object}
 */
export const parseNfoFundsResponse = (response) => {
  if (!response || !response?.result) {
    return {
      success: false,
      message: response?.message ?? 'Failed to fetch NFO funds',
      code: response?.code ?? 'UNKNOWN_ERROR',
      funds: [],
      total: 0,
      requestId: response?.requestId ?? null,
    };
  }

  const code = response?.code ?? null;
  const statusCode = response?.statusCode ?? null;
  const message = response?.message ?? '';
  const requestId = response?.requestId ?? null;

  const isSuccess =
    statusCode === 200 ||
    statusCode === '200' ||
    code === 'MFDS0012' ||
    code === 200 ||
    code === '200';

  const fundsArray = Array.isArray(response?.result?.data)
    ? response.result.data
    : Array.isArray(response?.result)
      ? response.result
      : [];

  const funds = (fundsArray ?? [])
    .map((fund) => parseNfoFundItem(fund))
    .filter((fund) => fund !== null && fund?.fundId !== null && fund?.fundId !== '')
    .filter((fund, index, arr) => arr.findIndex((item) => item?.fundId === fund?.fundId) === index);

  return {
    success: isSuccess || (funds?.length ?? 0) > 0,
    message: message ?? 'NFO funds retrieved successfully',
    code: code ?? null,
    funds: funds ?? [],
    total: funds?.length ?? 0,
    requestId: requestId ?? null,
  };
};
